<section class="h-full flex flex-col overflow-hidden p-6">
  <!-- Header row + legend -->
  <div class="flex items-start justify-between gap-6">
    <div class="shrink-0">
      <h1 class="oswald-font text-3xl text-nside-gray-400">
        Add Users to <span class="text-nside-orange-400">nSide|Lockdown</span>
      </h1>
      <p class="mt-1 text-sm text-gray-500">Search and select users to add to this application</p>
    </div>

    <!-- Access type info popover -->
    <div class="relative group self-center">
      <button type="button" class="flex items-center gap-1.5 text-xs font-semibold hover:text-orange-600 transition-colors text-nside-orange-400">
        <lucide-icon [img]="icons.Info" [size]="16"></lucide-icon>
        User Access Types
      </button>
      <div class="pointer-events-none absolute right-0 top-full mt-2 hidden group-hover:block w-72 rounded-lg border border-gray-200 bg-white shadow-lg z-30">
        <div class="flex flex-col divide-y">
          <div class="p-4 flex items-start gap-3">
            <lucide-icon [img]="icons.User" [size]="18" class="mt-0.5 shrink-0 text-blue-500"></lucide-icon>
            <div>
              <p class="text-xs font-semibold text-gray-700">App User</p>
              <p class="mt-0.5 text-[11px] text-gray-500">Can sign into the app, submit alerts, and update their status.</p>
            </div>
          </div>
          <div class="p-4 flex items-start gap-3">
            <lucide-icon [img]="icons.Mail" [size]="18" class="mt-0.5 shrink-0 text-gray-400"></lucide-icon>
            <div>
              <p class="text-xs font-semibold text-gray-700">Message Only</p>
              <p class="mt-0.5 text-[11px] text-gray-500">Will only receive messages and notifications from the app.</p>
            </div>
          </div>
          <div class="p-4 flex items-start gap-3">
            <lucide-icon [img]="icons.ShieldAlert" [size]="18" class="mt-0.5 shrink-0 text-nside-orange-400"></lucide-icon>
            <div>
              <p class="text-xs font-semibold text-gray-700">Admin App User</p>
              <p class="mt-0.5 text-[11px] text-gray-500">Full access — can send Administrative messages and request Status Checks.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="mt-3 flex flex-col min-h-0 rounded-lg bg-white p-6 shadow-custom-1">

    <!-- Table header row -->
    <div class="flex items-center justify-between">
      <p class="oswald-font text-lg text-nside-gray-400">Users Added ({{ users.length }})</p>
      <div class="flex gap-2">
        <button
          *ngIf="!userDataService.isOrg() || filterClientId === userDataService.buildingId()"
          type="button"
          (click)="openAddModal()"
          class="flex items-center gap-1.5 rounded-md bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 px-3 py-1.5 text-sm text-white oswald-font hover:from-nside-orange-400 hover:to-nside-orange-400">
          <lucide-icon [img]="icons.UserPlus" [size]="16"></lucide-icon>
          Add User
        </button>
        <div class="relative group/export" (mousemove)="onExportMouseMove($event)">
          <button
            type="button"
            (click)="exportPdf()"
            class="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 oswald-font hover:bg-gray-50">
            <lucide-icon [img]="icons.Download" [size]="16"></lucide-icon>
            Export
          </button>
          <div *ngIf="userDataService.isOrg()"
            class="fixed z-50 w-52 px-3 py-2 text-xs text-white bg-gray-700 rounded-lg shadow-sm invisible opacity-0 group-hover/export:visible group-hover/export:opacity-100 transition-opacity pointer-events-none -translate-y-full"
            [style.left.px]="exportTooltipX - 222"
            [style.top.px]="exportTooltipY - 8">
            Exports users for {{ exportBuildingName }}
          </div>
        </div>
        <button
          *ngIf="!userDataService.isOrg() || filterClientId === userDataService.buildingId()"
          type="button"
          (click)="openLogModal()"
          class="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 oswald-font hover:bg-gray-50">
          <lucide-icon [img]="icons.History" [size]="16"></lucide-icon>
          Log
        </button>
      </div>
    </div>

    <!-- Search + Access Type filter -->
    <div class="mt-3 flex items-center gap-2">
      <select
        [(ngModel)]="filterAccessType"
        (change)="filterUsers()"
        class="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-blue-500">
        <option value="">All Access Types</option>
        <option *ngFor="let t of accessTypeOptions" [value]="t">{{ t }}</option>
      </select>
      <select [class.hidden]="!userDataService.isOrg()"
        [(ngModel)]="filterClientId"
        (ngModelChange)="onClientFilterChange()"
        class="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-blue-500">
        <option *ngFor="let c of clientOptions; trackBy: trackByClientId" [value]="c.id">{{ c.name }}</option>
      </select>
      <div class="relative flex-1">
        <div class="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
          <lucide-icon [img]="icons.Search" [size]="16"></lucide-icon>
        </div>
        <input
          type="search"
          [(ngModel)]="searchQuery"
          (input)="filterUsers()"
          placeholder="Search users..."
          class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2 ps-9 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500" />
      </div>
    </div>

    <!-- Table -->
    <div class="mt-3 overflow-y-auto min-h-0">
      <ng-container *ngIf="filteredUsers.length > 0; else emptyUsers">
        <table class="w-full text-left">
          <thead class="oswald-font text-gray-700 uppercase bg-main-background sticky top-0 z-10">
            <tr>
              <th scope="col" class="px-4 py-3 w-14">No.</th>
              <th scope="col" class="px-4 py-3 min-w-40">
                <button type="button" (click)="sortBy('name')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  NAME <lucide-icon [img]="icons.ArrowUpDown" [size]="14"></lucide-icon>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 min-w-48">
                <button type="button" (click)="sortBy('email')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  EMAIL <lucide-icon [img]="icons.ArrowUpDown" [size]="14"></lucide-icon>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 min-w-36">
                <button type="button" (click)="sortBy('department')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  POSITION <lucide-icon [img]="icons.ArrowUpDown" [size]="14"></lucide-icon>
                </button>
              </th>
<th scope="col" class="px-4 py-3 min-w-36">
                <button type="button" (click)="sortBy('accessType')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  ACCESS TYPE <lucide-icon [img]="icons.ArrowUpDown" [size]="14"></lucide-icon>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 min-w-36">SMS CONSENT</th>
              <th scope="col" class="px-4 py-3 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let user of filteredUsers | paginate: { itemsPerPage: itemsPerPage, currentPage: page }; let i = index"
              class="border-b"
              [ngClass]="(isInherited(user) || !isCurrentBuilding(user)) ? 'bg-gray-50 cursor-not-allowed group/row' : 'bg-white hover:bg-gray-50'"
              (mousemove)="(isInherited(user) || !isCurrentBuilding(user)) && onRowMouseMove($event)">
              <td class="px-4 py-3 text-gray-500">
                {{ i + 1 + (page - 1) * itemsPerPage }}
              </td>
              <td class="px-4 py-3 font-medium whitespace-nowrap" [ngClass]="isInherited(user) ? 'text-gray-400' : 'text-gray-900'">
                {{ user.name }}
                <span *ngIf="isInherited(user)" class="ml-1.5 rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-normal text-gray-500">Inherited</span>
              </td>
              <td class="px-4 py-3" [ngClass]="isInherited(user) ? 'text-gray-400' : 'text-gray-600'">{{ user.email }}</td>
              <td class="px-4 py-3" [ngClass]="isInherited(user) ? 'text-gray-400' : 'text-gray-600'">{{ user.department }}</td>
<td class="px-4 py-3">
                <ng-container *ngIf="!isInherited(user) && !user.isContact && isCurrentBuilding(user); else lockedBadge">
                  <select
                    [ngModel]="user.accessType"
                    (ngModelChange)="onAccessTypeChange(user, $event)"
                    class="rounded-full px-2.5 py-0.5 text-xs font-medium border-0 cursor-pointer focus:ring-1 focus:ring-nside-orange-400"
                    [ngClass]="accessTypeBadgeClass(user.accessType)">
                    <option *ngFor="let type of accessTypeOptions" [value]="type">{{ type }}</option>
                  </select>
                </ng-container>
                <ng-template #lockedBadge>
                  <div class="flex items-center gap-1.5">
                    <span class="rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-500">
                      {{ user.accessType }}
                    </span>
                    <div *ngIf="isInherited(user)" class="relative">
                      <div class="fixed z-50 w-52 px-3 py-2 text-xs text-white bg-gray-700 rounded-lg shadow-sm invisible opacity-0 group-hover/row:visible group-hover/row:opacity-100 transition-opacity pointer-events-none -translate-y-full"
                        [style.left.px]="tooltipX + 14"
                        [style.top.px]="tooltipY - 8">
                        Inherited from an organization level — go to that building to manage this user.
                      </div>
                    </div>
                    <div *ngIf="!isInherited(user) && !isCurrentBuilding(user) && !user.inherited" class="relative">
                      <div class="fixed z-50 w-56 px-3 py-2 text-xs text-white bg-gray-700 rounded-lg shadow-sm invisible opacity-0 group-hover/row:visible group-hover/row:opacity-100 transition-opacity pointer-events-none -translate-y-full"
                        [style.left.px]="tooltipX + 14"
                        [style.top.px]="tooltipY - 8">
                        This user belongs to {{ user.clientName ?? 'another building' }} — switch to that building to manage them.
                      </div>
                    </div>
                    <div *ngIf="!isInherited(user) && !isCurrentBuilding(user) && user.inherited" class="relative">
                      <div class="fixed z-50 w-56 px-3 py-2 text-xs text-white bg-gray-700 rounded-lg shadow-sm invisible opacity-0 group-hover/row:visible group-hover/row:opacity-100 transition-opacity pointer-events-none -translate-y-full"
                        [style.left.px]="tooltipX + 14"
                        [style.top.px]="tooltipY - 8">
                        <ng-container *ngIf="userDataService.isOrg(); else inheritedBuildingMsg">
                          This user's access at {{ user.clientName ?? 'another building' }} is inherited.
                        </ng-container>
                        <ng-template #inheritedBuildingMsg>
                          Inherited from an organization level — switch to that building to manage this user.
                        </ng-template>
                      </div>
                    </div>
                    <div *ngIf="!isInherited(user) && user.isContact && isCurrentBuilding(user)" class="relative group">
                      <lucide-icon [img]="icons.CircleAlert" [size]="14" class="text-gray-400 cursor-help"></lucide-icon>
                      <div class="fixed z-50 mb-2 w-52 px-3 py-2 text-xs text-white bg-gray-700 rounded-lg shadow-sm invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity pointer-events-none -translate-x-1/2 -translate-y-full">
                        Building Contact — permission is locked to Message Only
                        <div class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-700"></div>
                      </div>
                    </div>
                  </div>
                </ng-template>
              </td>
              <td class="px-4 py-3">
                <span *ngIf="user.smsConsent !== 2" class="rounded-full px-2.5 py-0.5 text-xs font-medium" [ngClass]="smsConsentBadgeClass(user)">
                  {{ smsConsentLabel(user) }}
                </span>
                <div *ngIf="user.smsConsent === 2" class="relative group inline-block">
                  <span class="rounded-full px-2.5 py-0.5 text-xs font-medium cursor-help" [ngClass]="smsConsentBadgeClass(user)">
                    {{ smsConsentLabel(user) }}
                  </span>
                  <div class="fixed z-50 mb-2 w-56 px-3 py-2 text-xs text-white bg-gray-700 rounded-lg shadow-sm invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity pointer-events-none -translate-x-1/2 -translate-y-full">
                    Text "START" to (334) 310-5592 to confirm
                    <div class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-700"></div>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3">
                <button
                  *ngIf="!isInherited(user) && isCurrentBuilding(user)"
                  type="button"
                  (click)="promptRemoveUser(user)"
                  class="flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-500 hover:bg-red-50">
                  <lucide-icon [img]="icons.X" [size]="12"></lucide-icon>
                  Remove
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </ng-container>

      <ng-template #emptyUsers>
        <div class="py-10 text-center text-sm italic text-gray-400">
          No users added yet. Click "Add User" to get started.
        </div>
      </ng-template>
    </div>

    <!-- Pagination -->
    <div class="mt-3 flex items-center justify-between">
      <p class="text-sm text-gray-500">{{ filteredUsers.length }} in total</p>
      <pagination-controls
        *ngIf="filteredUsers.length > itemsPerPage"
        (pageChange)="page = $event"
        previousLabel="Prev"
        nextLabel="Next">
      </pagination-controls>
    </div>

  </div>
</section>

<app-add-user-modal
  *ngIf="showAddModal"
  [buildingContacts]="buildingContacts"
  [contacts]="contacts"
  [addedIds]="addedIds"
  [loading]="loadingModal"
  (userAdded)="onUserAdded($event)"
  (closed)="onModalClosed()">
</app-add-user-modal>

<app-activity-log-modal
  *ngIf="showLogModal"
  [logs]="logs"
  [loading]="loadingLogs"
  (closed)="showLogModal = false">
</app-activity-log-modal>

<spinner *ngIf="loading || saving"></spinner>

<app-confirm-modal
  *ngIf="confirmModal.visible"
  [title]="confirmModal.action === 'single' ? 'Remove User' : 'Remove All Users'"
  [message]="confirmModal.action === 'single'
    ? 'Are you sure you want to remove <strong>' + confirmModal.user?.name + '</strong> from this application?'
    : 'Are you sure you want to remove <strong>all ' + users.length + ' users</strong> from this application? This cannot be undone.'"
  [confirmLabel]="confirmModal.action === 'single' ? 'Remove' : 'Remove All'"
  (confirmed)="confirmAction()"
  (cancelled)="cancelConfirm()">
</app-confirm-modal>
